import { useCallback, useEffect, useState } from "react";
import client from "@/lib/api";

const CONDITION_STYLE = {
  Excellent: "bg-[#D1FAE5] text-[#065F46] border-[#34D399]",
  Good: "bg-[#DBEAFE] text-[#1E40AF] border-[#60A5FA]",
  Fair: "bg-[#FEF3C7] text-[#92400E] border-[#FBBF24]",
  Poor: "bg-[#DCFCE7] text-[#14532D] border-[#86EFAC]",
  Damaged: "bg-[#FEE2E2] text-[#991B1B] border-[#F87171]",
  "Not Applicable": "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]",
  "—": "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]",
};

export default function Compare() {
  const [inspections, setInspections] = useState([]);
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [diff, setDiff] = useState(null);
  const [props, setProps] = useState({});

  const loadInitial = useCallback(async () => {
    const [i, p] = await Promise.all([client.get("/inspections"), client.get("/properties")]);
    setInspections(i.data);
    const m = {};
    p.data.forEach((x) => (m[x.id] = x));
    setProps(m);
  }, []);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const compare = useCallback(async () => {
    if (!a || !b) return;
    const { data } = await client.get(`/inspections/${a}/compare/${b}`);
    setDiff(data);
  }, [a, b]);

  const label = (i) =>
    `${props[i.property_id]?.address || "—"} · ${i.inspection_type} · ${i.inspection_date}`;

  return (
    <div data-testid="compare-page">
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#6B7280] mb-1">Comparison</div>
      <h1 className="trent-heading text-3xl md:text-4xl font-black tracking-tighter text-[#1E3A5F] mb-6">
        Move-in vs Move-out
      </h1>

      <div className="trent-card p-6 mb-6 grid md:grid-cols-3 gap-3 items-end">
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Before (e.g. Move-In)</span>
          <select value={a} onChange={(e) => setA(e.target.value)} className="trent-input mt-1" data-testid="compare-a">
            <option value="">Select inspection</option>
            {inspections.map((i) => (
              <option key={i.id} value={i.id}>{label(i)}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">After (e.g. Move-Out)</span>
          <select value={b} onChange={(e) => setB(e.target.value)} className="trent-input mt-1" data-testid="compare-b">
            <option value="">Select inspection</option>
            {inspections.map((i) => (
              <option key={i.id} value={i.id}>{label(i)}</option>
            ))}
          </select>
        </label>
        <button onClick={compare} disabled={!a || !b} className="trent-btn-primary" data-testid="compare-btn">
          Run comparison
        </button>
      </div>

      {diff && (
        <div className="trent-card overflow-hidden" data-testid="compare-result">
          <div className="px-4 py-3 bg-[#1E3A5F] text-white text-sm flex items-center justify-between">
            <span>
              {diff.changed_count} change{diff.changed_count === 1 ? "" : "s"} of {diff.items.length} items
            </span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-white/70">
              {diff.a.type} → {diff.b.type}
            </span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#F3F4F6] text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">
              <tr>
                <th className="text-left px-4 py-2">Room</th>
                <th className="text-left px-4 py-2">Item</th>
                <th className="text-left px-4 py-2">Before</th>
                <th className="text-left px-4 py-2">After</th>
              </tr>
            </thead>
            <tbody>
              {diff.items.map((d) => (
                <tr key={`${d.room}::${d.item}`} className={`border-t border-[#E5E7EB] ${d.changed ? "bg-[#F0FDF4]" : ""}`}>
                  <td className="px-4 py-2">{d.room}</td>
                  <td className="px-4 py-2 font-medium">{d.item}</td>
                  <td className="px-4 py-2"><span className={`condition-pill ${CONDITION_STYLE[d.before] || ""}`}>{d.before}</span></td>
                  <td className="px-4 py-2"><span className={`condition-pill ${CONDITION_STYLE[d.after] || ""}`}>{d.after}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
